import get from 'lodash/get.js';
import isEmpty from 'lodash/isEmpty.js';

import axios from '@/plugins/axios.js';

class Rubrics {
    getNamespace(namespace) {
        return namespace.replace(/\/.*/, '');
    }

    validateCriteriaRatings(rubric) {
        const criterias = get(rubric, 'criterias');

        return criterias.every(criteria => {
            if (!isEmpty(criteria.criteria_ratings)) {
                return criteria.criteria_ratings.every(rating => {
                    const value = typeof rating.value === 'number';

                    return value && rating.label && rating.description;
                });
            }

            return true;
        });
    }

    async pushRating(store, mutation) {
        const namespace = this.getNamespace(mutation.type);

        const rubric = store.getters[`${namespace}/get`](mutation.payload.target);

        const rubric_data = await store.dispatch('AutoSave/rubrics', {
            rubric,
            mutation,
        });

        if (!rubric_data) return;

        const ratings = rubric.criterias[mutation.payload.parent].criteria_ratings;

        await ratings.forEach((rating, index) => {
            if (rating.description === mutation.payload.value.description) {
                if (!rating.id) {
                    store.commit(`${namespace}/SET_RATING`, {
                        target: mutation.payload.target,
                        parent: mutation.payload.parent,
                        order: index,
                        field: 'id',
                        value: rubric_data.criterias[mutation.payload.parent].criteria_ratings[index].id,
                    });
                }
            }
        });
    }

    async pushRubricTemplateRating(store, mutation) {
        const namespace = this.getNamespace(mutation.type);

        const rubricBank = store.getters[`${namespace}/get`](mutation.payload.target);

        const rubricBankData = await store.dispatch('AutoSave/rubricBanks', {
            rubricBank,
            mutation,
        });

        if (!rubricBankData) return;

        const ratings = rubricBank.criterias[mutation.payload.parent].criteria_ratings;

        await ratings.forEach((rating, index) => {
            if (rating.description === mutation.payload.value.description) {
                if (!rating.id) {
                    store.commit(`${namespace}/SET_RATING`, {
                        target: mutation.payload.target,
                        parent: mutation.payload.parent,
                        order: index,
                        field: 'id',
                        value: rubricBankData.criterias[mutation.payload.parent].criteria_ratings[index].id,
                    });
                }
            }
        });
    }

    async setCriteria(store, mutation) {
        const namespace = this.getNamespace(mutation.type);

        const rubric = store.getters[`${namespace}/get`](mutation.payload.target);

        // Validate that criterias exist and all have valid max_value
        const hasValidCriterias = this.validateCriteriaMaxValues(rubric);

        if (!hasValidCriterias) {
            return;
        }

        const rubric_data = await store.dispatch('AutoSave/rubrics', {
            rubric,
            mutation,
        });

        if (!rubric_data) return;

        store.commit(`${namespace}/EDIT`, {
            target: rubric.uuid,
            field: 'id',
            value: rubric_data.id,
        });

        store.commit(`${namespace}/SET_CRITERIA`, {
            target: mutation.payload.target,
            order: mutation.payload.order,
            field: 'id',
            value: rubric_data.criterias[mutation.payload.order].id,
        });
    }

    async setRubricTemplateCriteria(store, mutation) {
        const namespace = this.getNamespace(mutation.type);

        const rubricBank = store.getters[`${namespace}/get`](mutation.payload.target);

        // Validate that criterias exist and all have valid max_value
        const hasValidCriterias = this.validateCriteriaMaxValues(rubricBank);

        if (!hasValidCriterias) {
            return;
        }

        const rubricBankData = await store.dispatch('AutoSave/rubricBanks', {
            rubricBank,
            mutation,
        });

        if (rubricBank.uuid) {
            store.commit(`${namespace}/EDIT`, {
                target: rubricBank.uuid,
                field: 'id',
                value: rubricBankData.id,
            });
        }

        if (rubricBankData.criterias) {
            if (rubricBankData.criterias.length > 0) {
                store.commit(`${namespace}/SET_CRITERIA`, {
                    target: mutation.payload.target,
                    order: mutation.payload.order,
                    field: 'id',
                    value: rubricBankData.criterias[mutation.payload.order].id,
                });
            }
        }
    }

    async setRating(store, mutation) {
        const namespace = this.getNamespace(mutation.type);

        const rubric = store.getters[`${namespace}/get`](mutation.payload.target);

        const isValid = this.validateCriteriaRatings(rubric);

        if (!isValid) return;

        const rubric_data = await store.dispatch('AutoSave/rubrics', {
            rubric,
            mutation,
        });

        if (!rubric_data) return;

        if (!rubric.criterias[mutation.payload.parent].criteria_ratings[mutation.payload.order].id) {
            store.commit(`${namespace}/SET_RATING`, {
                target: mutation.payload.target,
                parent: mutation.payload.parent,
                order: mutation.payload.order,
                field: 'id',
                value: rubric_data.criterias[mutation.payload.parent].criteria_ratings[mutation.payload.order].id,
            });
        }
    }

    async setRubricTemplateRating(store, mutation) {
        const namespace = this.getNamespace(mutation.type);

        const rubricBank = store.getters[`${namespace}/get`](mutation.payload.target);

        const isValid = this.validateCriteriaRatings(rubricBank);

        if (!isValid) return;

        const rubricData = await store.dispatch('AutoSave/rubricBanks', {
            rubricBank,
            mutation,
        });

        if (!rubricData) return;

        if (!rubricBank.criterias[mutation.payload.parent].criteria_ratings[mutation.payload.order].id) {
            store.commit(`${namespace}/SET_RATING`, {
                target: mutation.payload.target,
                parent: mutation.payload.parent,
                order: mutation.payload.order,
                field: 'id',
                value: rubricData.criterias[mutation.payload.parent].criteria_ratings[mutation.payload.order].id,
            });
        }
    }

    async removeCriteria(store, mutation) {
        const rubric = store.getters[`${this.getNamespace(mutation.type)}/get`](mutation.payload.target);

        await store.dispatch('AutoSave/rubrics', {
            rubric,
            mutation,
        });
    }

    async removeRating(store, mutation) {
        const rubric = store.getters[`${this.getNamespace(mutation.type)}/get`](mutation.payload.target);

        await store.dispatch('AutoSave/rubrics', {
            rubric,
            mutation,
        });
    }

    async save(store, mutation) {
        const namespace = this.getNamespace(mutation.type);

        const rubric = store.getters[`${namespace}/get`](mutation.payload.target);

        // Validate that criterias exist and all have valid max_value
        const hasValidCriterias = this.validateCriteriaMaxValues(rubric);

        if (!isEmpty(get(rubric, 'criterias')) && hasValidCriterias) {
            const rubric_data = await store.dispatch('AutoSave/rubrics', {
                rubric,
                mutation,
            });

            if (!rubric_data) return;

            store.commit(`${namespace}/EDIT`, {
                target: rubric.uuid,
                field: 'id',
                value: rubric_data.id,
            });

            if (!isEmpty(rubric_data.criterias)) {
                store.commit(`${namespace}/EDIT`, {
                    target: rubric.uuid,
                    field: 'criterias',
                    value: rubric_data.criterias,
                });
            }
        }
    }

    validateCriteriaMaxValues(rubricBank) {
        const criterias = get(rubricBank, 'criterias');

        if (isEmpty(criterias)) {
            return false;
        }

        // Check that all criteria have max_value > 0 and that no rating exceeds the max_value
        return criterias.every(criteria => {
            const maxValue = criteria.max_value;

            // Check if max_value is valid (not null, undefined, and > 0)
            if (maxValue === null || maxValue === undefined || Number(maxValue) <= 0) {
                return false;
            }

            // Check if any rating value exceeds the criteria max_value
            const ratings = get(criteria, 'criteria_ratings', []);
            if (!isEmpty(ratings)) {
                const hasInvalidRating = ratings.some(rating => {
                    return Number(rating.value) > Number(maxValue);
                });

                if (hasInvalidRating) {
                    return false;
                }
            }

            return true;
        });
    }

    async saveRubricTemplate(store, mutation) {
        const namespace = this.getNamespace(mutation.type);

        const rubricBank = store.getters[`${namespace}/get`](mutation.payload.target);

        // Validate that criterias exist and all have valid max_value
        const hasValidCriterias = this.validateCriteriaMaxValues(rubricBank);

        if (!isEmpty(get(rubricBank, 'criterias')) && hasValidCriterias) {
            const rubricBankData = await store.dispatch('AutoSave/rubricBanks', {
                rubricBank,
                mutation,
            });

            if (!rubricBankData) {
                return;
            }

            if (rubricBank.id === null) {
                store.commit(`${namespace}/EDIT`, {
                    target: rubricBank.uuid,
                    field: 'id',
                    value: rubricBankData.id,
                });
            }
        }
    }

    async delete(store, mutation) {
        const hasSubmissions = mutation.payload.has_submissions ? 1 : 0;

        store.dispatch('AutoSave/setSaving', mutation);

        const template = mutation.type.toLowerCase().includes('temp');

        const apiRoute = `/api/rubric${template ? '-template' : ''}s/${
            mutation.payload.rubric.id
        }?has_submissions=${hasSubmissions}`;

        await axios.delete(apiRoute);

        store.dispatch('AutoSave/setSaved', mutation);
    }

    async removeRubricTemplateRating(store, mutation) {
        const rubricBank = store.getters[`${this.getNamespace(mutation.type)}/get`](mutation.payload.target);

        await store.dispatch('AutoSave/rubricBanks', {
            rubricBank,
            mutation,
        });
    }

    async removeRubricTemplateCriteria(store, mutation) {
        const rubricBank = store.getters[`${this.getNamespace(mutation.type)}/get`](mutation.payload.target);

        await store.dispatch('AutoSave/rubricBanks', {
            rubricBank,
            mutation,
        });
    }
}

export default Rubrics;
